"use client"
import {
  FlexCenter,
  Media,
  Separator,
  SkeletonLine,
  TextLabel,
  tv,
} from "@opensea/ui-kit"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import type { ListingFlowFormValues } from "../../types"
import { AdjustForFeesSwitch } from "./AdjustForFeesSwitch"
import { FloorButton } from "./FloorButton"
import { LadderButton } from "./LadderButton"
import { ReserveButton } from "./ReserveButton"
import { SetPriceInput } from "./SetPriceInput"
import { TraitFloorButton } from "./TraitFloorButton"

type Props = {
  className?: string
  listingSymbol: string
}

const actionsVariants = tv({
  base: "grid gap-3 md:mr-10 md:flex xl:mr-0",
  variants: {
    bulk: {
      true: "grid-cols-4",
      false: "grid-cols-3",
    },
  },
})

export const ListingFlowHeaderActions = ({
  className,
  listingSymbol,
}: Props) => {
  const t = useTranslations("wallet.ListingFlowHeaderActions")
  const { control } = useFormContext<ListingFlowFormValues>()
  const listings = useWatch({ control, name: "listings" })

  return (
    <FlexCenter
      className={actionsVariants({ bulk: listings.length > 1, className })}
    >
      <ReserveButton />
      <Media greaterThanOrEqual="md">
        <AdjustForFeesSwitch />
        <Separator className="h-8" orientation="vertical" />
        <TextLabel className="whitespace-nowrap" size="2xs">
          {t("setPriceLabel", { count: listings.length })}
        </TextLabel>
      </Media>
      <FloorButton />
      <TraitFloorButton />
      <LadderButton />
      <SetPriceInput listingSymbol={listingSymbol} />
    </FlexCenter>
  )
}

export const ListingFlowHeaderActionsSkeleton = ({
  className,
  count,
}: {
  className?: string
  count: number
}) => {
  const t = useTranslations("wallet.ListingFlowHeaderActions")
  return (
    <FlexCenter className={actionsVariants({ bulk: count > 1, className })}>
      <Media greaterThanOrEqual="md">
        <SkeletonLine className="h-8 w-[76px]" />
        <SkeletonLine className="h-8 w-[120px]" />
        <Separator className="h-8" orientation="vertical" />
        <TextLabel className="whitespace-nowrap" size="2xs">
          {t("setPriceLabel", { count })}
        </TextLabel>
      </Media>
      <SkeletonLine className="h-8 w-full md:w-[58px]" />
      <SkeletonLine className="h-8 w-full md:w-[88px]" />
      <SkeletonLine className="h-8 w-full md:w-[68px]" />
      <SkeletonLine className="h-8 w-full md:w-[120px]" />
    </FlexCenter>
  )
}
